/**
 * lib/module-templates/template-layouts.ts — wspólna warstwa huba Kreatora dla
 * projektowania layoutów szablonu (Krok 2): szczegół szablonu (React Query),
 * zapis configu widoku przez PATCH (szew z Kroku 0), mapa typów pól
 * szablon↔builder, zasiew kolumn widoku listy i identyfikatory `tableId`
 * trybu `template` dla Kreatora Listy.
 *
 * Zapis WYŁĄCZNIE na żądanie: PATCH
 * `/api/admin/module-templates/[code]/entities/[entitySlug]/views/[viewSlug]/config`
 * — jedna transakcja (config + bump wersji technicznej + snapshot), dedup no-op,
 * 409 `NO_STRUCTURE` / `VERSION_CONFLICT`.
 */

import {
  type ColumnConfig,
  type ListConfig,
  listConfigSchema,
  type MenuItem,
  type ModuleTemplateDefinition,
  type ModuleTemplateDraftMenu,
  type ModuleTemplateEntityDefinition,
  type ModuleTemplateFieldType,
  type ModuleTemplateFormConfig,
  type TemplateMenuSourceResponse,
} from '@saas/shared';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { api } from '@/lib/api';

// ─── Szczegół szablonu (ten sam cache co ModuleTemplateEditor) ───────────────

export function moduleTemplateDetailQueryKey(code: string) {
  return ['admin', 'module-templates', code] as const;
}

/** Pełna definicja szablonu z ADM — klucz współdzielony z edytorem struktury. */
export function useModuleTemplateDetail(
  code: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery<ModuleTemplateDefinition>({
    queryKey: moduleTemplateDetailQueryKey(code ?? ''),
    queryFn: async () => (await api.get(`/admin/module-templates/${code}`)).data,
    enabled: (options.enabled ?? true) && Boolean(code),
    retry: false,
  });
}

/**
 * Kartoteki do palety kreatora menu modułu (systemowe + z opublikowanych modułów kreatora).
 * Własne kartoteki edytowanego modułu i ekrany składa kreator lokalnie.
 */
export function useTemplateMenuSource(code: string | undefined) {
  return useQuery<TemplateMenuSourceResponse>({
    queryKey: ['admin', 'module-templates', code ?? '', 'menu-source'],
    queryFn: async () => (await api.get(`/admin/module-templates/${code}/menu-source`)).data,
    enabled: Boolean(code),
    retry: false,
    staleTime: 60_000,
  });
}

// ─── Zapis configu widoku (szew z Kroku 0) ───────────────────────────────────

export type TemplateViewConfigPatchBody =
  | { listConfig: ListConfig }
  | { formConfig: ModuleTemplateFormConfig };

/**
 * PATCH configu widoku szablonu. Sukces: `{ technicalVersion, unchanged }`
 * (dedup no-op nie bumpuje wersji). Błędy mapuj przez `extractTemplateApiErrorCode`.
 */
export async function patchTemplateViewConfig(
  code: string,
  entitySlug: string,
  viewSlug: string,
  body: TemplateViewConfigPatchBody,
): Promise<{ technicalVersion: string; unchanged: boolean }> {
  const res = await api.patch(
    `/admin/module-templates/${code}/entities/${entitySlug}/views/${viewSlug}/config`,
    body,
  );
  return res.data as { technicalVersion: string; unchanged: boolean };
}

/**
 * Nazwany layout zapisywany razem z menu (builder pozycji menu). Każdy upsertowany
 * w entity.views; pozycja menu wskazuje go przez `viewRef` (formularz) lub `listRef` (lista).
 * Discriminated union po `viewType`.
 */
export type TemplateMenuViewUpsert =
  | {
      viewType: 'form';
      entitySlug: string;
      slug: string;
      name: string;
      formConfig: ModuleTemplateFormConfig;
      sortOrder?: number;
      /** Układ kartoteki SPOZA modułu (wspólna biblioteka kartoteki); `entitySlug` = ten kod. */
      targetModuleCode?: string;
    }
  | {
      viewType: 'list';
      entitySlug: string;
      slug: string;
      name: string;
      listConfig: ListConfig;
      sortOrder?: number;
      targetModuleCode?: string;
    };

/**
 * Zapis domyślnego menu modułu (kreator/hub) WRAZ z nazwanymi layoutami (formularz i lista,
 * opcjonalne `views`). PATCH bumpuje wersję techniczną i zapisuje snapshot
 * `{ entities, defaultMenu }` (encje z wmieszanymi layoutami) — atomowo, jedną
 * transakcją. Sukces: `{ hierarchy, technicalVersion, viewsSaved }`. Błędy (np.
 * VERSION_CONFLICT, walidacja) mapuj przez `extractTemplateApiErrorCode`.
 */
export async function patchTemplateMenu(
  code: string,
  hierarchy: MenuItem[],
  views?: TemplateMenuViewUpsert[],
): Promise<{ hierarchy: MenuItem[]; technicalVersion: string; viewsSaved?: number }> {
  const res = await api.patch(`/admin/module-templates/${code}/menu`, {
    hierarchy,
    ...(views && views.length > 0 ? { views } : {}),
  });
  return res.data as { hierarchy: MenuItem[]; technicalVersion: string; viewsSaved?: number };
}

// ---------------------------------------------------------------------------
// Menu ROBOCZE modułu (szkice ITE w bazie, NIE jadą do klientów). Do klientów jedzie wybrane
// menu (`patchTemplateMenu`); „Wybierz" (`selectDraftMenu`) zamienia robocze z wybranym.
// ---------------------------------------------------------------------------

/** Nowe, puste menu robocze. Błędy: MENU_NAME_TAKEN (409), MENU_LIMIT_REACHED (422). */
export async function createDraftMenu(
  code: string,
  name: string,
): Promise<ModuleTemplateDraftMenu> {
  const res = await api.post(`/admin/module-templates/${code}/menus`, { name });
  return (res.data as { menu: ModuleTemplateDraftMenu }).menu;
}

/**
 * Zapis menu roboczego — nazwa i/lub treść (z nazwanymi layoutami projektowanymi przy
 * pozycjach, jak przy wybranym menu). Nie podbija wersji szablonu.
 */
export async function patchDraftMenu(
  code: string,
  menuId: string,
  patch: { name?: string; hierarchy?: MenuItem[]; views?: TemplateMenuViewUpsert[] },
): Promise<ModuleTemplateDraftMenu> {
  const { views, ...rest } = patch;
  const res = await api.patch(`/admin/module-templates/${code}/menus/${menuId}`, {
    ...rest,
    ...(views && views.length > 0 ? { views } : {}),
  });
  return (res.data as { menu: ModuleTemplateDraftMenu }).menu;
}

export async function deleteDraftMenu(code: string, menuId: string): Promise<void> {
  await api.delete(`/admin/module-templates/${code}/menus/${menuId}`);
}

/**
 * „Wybierz" — menu robocze staje się wybranym (jadącym do klientów po publikacji), poprzednie
 * wybrane wraca jako robocze. Bump wersji technicznej. Błędy: VIEW_REF_NOT_FOUND (422),
 * VERSION_CONFLICT (409), walidacja paletki (400).
 */
export async function selectDraftMenu(code: string, menuId: string): Promise<void> {
  await api.post(`/admin/module-templates/${code}/menus/${menuId}/select`);
}

/**
 * Etap 2 kartoteki — zapis per-modułowego override layoutu formularza kartoteki systemowej.
 * PUT /api/admin/module-templates/[code]/entities/[entitySlug]/kartoteka-layout
 *
 * Sukces: `{ saved: true, entitySlug, kartotekaLayoutOverride }`.
 * Błędy: 404 TEMPLATE_NOT_FOUND / ENTITY_NOT_FOUND, 400 NOT_A_REFERENCE_ENTITY / VALIDATION_FAILED.
 */
export async function putKartotekaLayoutOverride(
  code: string,
  entitySlug: string,
  layoutConfig: Record<string, unknown>,
): Promise<{ saved: true; entitySlug: string; kartotekaLayoutOverride: Record<string, unknown> }> {
  const res = await api.put(
    `/admin/module-templates/${code}/entities/${entitySlug}/kartoteka-layout`,
    { layoutConfig },
  );
  return res.data as {
    saved: true;
    entitySlug: string;
    kartotekaLayoutOverride: Record<string, unknown>;
  };
}

/**
 * Wysyła zapisany override układu formularza kartoteki do wszystkich aktywnych klientów,
 * którzy mają zainstalowany ten szablon.
 *
 * POST /api/admin/module-templates/[code]/entities/[entitySlug]/publish-kartoteka-layout
 *
 * Sukces: `{ dryRun, totals: { tenants, succeeded, failed, fieldsCreated }, tenants: [...] }`.
 */
export interface PublishKartotekaLayoutTenantOutcome {
  tenantId: string;
  code: string;
  fieldsCreated?: string[];
  fieldsSkipped?: string[];
  layoutsApplied?: ('form' | 'list')[];
  error?: string;
}

export async function publishKartotekaLayout(
  code: string,
  entitySlug: string,
): Promise<{
  dryRun: boolean;
  totals: { tenants: number; succeeded: number; failed: number; fieldsCreated: number };
  tenants: Array<PublishKartotekaLayoutTenantOutcome>;
  /** ETAP4 S7: ostemplowany override (znaczniki publishedAt zakładek) do synchronizacji edytora. */
  kartotekaLayoutOverride?: Record<string, unknown>;
}> {
  const res = await api.post(
    `/admin/module-templates/${code}/entities/${entitySlug}/publish-kartoteka-layout`,
  );
  return res.data as {
    dryRun: boolean;
    totals: { tenants: number; succeeded: number; failed: number; fieldsCreated: number };
    tenants: Array<PublishKartotekaLayoutTenantOutcome>;
    kartotekaLayoutOverride?: Record<string, unknown>;
  };
}

/**
 * Wysyła zapisany WIDOK LISTY kartoteki (kolumny + kolejność) do wszystkich aktywnych
 * klientów, którzy mają zainstalowany ten moduł. Bliźniak `publishKartotekaLayout`
 * (formularz) — osobny przycisk „Wyślij widok listy" (decyzja produktowa W4 Faza 1b).
 *
 * POST /api/admin/module-templates/[code]/entities/[entitySlug]/publish-kartoteka-list
 *
 * Sukces: `{ dryRun, totals: { tenants, succeeded, failed, fieldsCreated }, tenants: [...] }`.
 * 422 NO_LIST_VIEW gdy widok listy nieuzupełniony.
 */
export async function publishKartotekaList(
  code: string,
  entitySlug: string,
): Promise<{
  dryRun: boolean;
  totals: { tenants: number; succeeded: number; failed: number; fieldsCreated: number };
  tenants: Array<PublishKartotekaLayoutTenantOutcome>;
}> {
  const res = await api.post(
    `/admin/module-templates/${code}/entities/${entitySlug}/publish-kartoteka-list`,
  );
  return res.data as {
    dryRun: boolean;
    totals: { tenants: number; succeeded: number; failed: number; fieldsCreated: number };
    tenants: Array<PublishKartotekaLayoutTenantOutcome>;
  };
}

// ─── Parsowanie referencji do nazwanych widoków (viewRef / listRef) ──────────

/**
 * Parsuje ref w formacie 'entitySlug:viewSlug' na składowe.
 * Zwraca null gdy ref ma nieprawidłowy format (brak separatora lub pusty człon).
 * Używany przez resolveViewLabels (MenuBuilderEmbedded) i assignedSlug (MenuItemInlineEditor).
 */
export function parseMenuViewRef(ref: string): { entitySlug: string; viewSlug: string } | null {
  const sep = ref.indexOf(':');
  if (sep <= 0) return null;
  const entitySlug = ref.slice(0, sep);
  const viewSlug = ref.slice(sep + 1);
  if (!viewSlug) return null;
  return { entitySlug, viewSlug };
}

/** Kod błędu API (`{ error: '...' }`) — np. VERSION_CONFLICT / NO_STRUCTURE. */
export function extractTemplateApiErrorCode(err: unknown): string | undefined {
  const ax = err as AxiosError<{ error?: string }>;
  return ax.response?.data?.error;
}

// ─── Mapa typów pól: szablon → builder ───────────────────────────────────────

/**
 * Slugi typów pól szablonu (`MODULE_TEMPLATE_FIELD_TYPES`) ≠ slugi typów buildera
 * (`FieldRegistry`). Mapowane są te o innych nazwach; reszta przechodzi 1:1.
 */
export const TEMPLATE_TO_BUILDER_FIELD_TYPE: Record<ModuleTemplateFieldType, string> = {
  text: 'text',
  longtext: 'textarea',
  number: 'number',
  decimal: 'decimal',
  date: 'date',
  datetime: 'datetime',
  boolean: 'checkbox',
  select: 'select',
  multi_select: 'multiselect',
  reference: 'relation',
  file: 'file',
  image: 'image',
};

// ─── tableId trybu `template` dla Kreatora Listy ─────────────────────────────

const TEMPLATE_TABLE_ID_PREFIX = 'template:';

/**
 * Identyfikator listingu widoku `list` szablonu — trzeci wariant obok
 * `module-<id>` i slugów systemowych. Separator `:` jest bezpieczny: kody
 * szablonów i slugi encji/widoków to `[a-z][a-z0-9_]*`.
 */
export function templateTableId(code: string, entitySlug: string, viewSlug: string): string {
  return `${TEMPLATE_TABLE_ID_PREFIX}${code}:${entitySlug}:${viewSlug}`;
}

export function isTemplateTableId(tableId: string): boolean {
  return tableId.startsWith(TEMPLATE_TABLE_ID_PREFIX);
}

export function parseTemplateTableId(
  tableId: string,
): { code: string; entitySlug: string; viewSlug: string } | null {
  if (!isTemplateTableId(tableId)) return null;
  const [code, entitySlug, viewSlug] = tableId.slice(TEMPLATE_TABLE_ID_PREFIX.length).split(':');
  if (!code || !entitySlug || !viewSlug) return null;
  return { code, entitySlug, viewSlug };
}

// ─── Zasiew kolumn widoku listy ──────────────────────────────────────────────

/**
 * Pierwsze otwarcie Kreatora Listy dla widoku bez configu: panele nie mają
 * „dodaj kolumnę", więc zasiewamy komplet — wbudowane `record_name` +
 * `record_number`, potem wszystkie pola encji w kolejności definicji.
 * BEZ `category` (byt tenanta — kontrakt z Kroku 0 jej zakazuje).
 */
export function seedTemplateListConfig(entity: ModuleTemplateEntityDefinition): ListConfig {
  const fieldColumns: ColumnConfig[] = [...entity.fields]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((field, index) => ({
      id: field.slug,
      visible: true,
      width: 'auto',
      order: index + 2,
    }));
  const seeded = {
    columns: [
      { id: 'record_name', visible: true, width: 'auto', order: 0 },
      { id: 'record_number', visible: true, width: 'auto', order: 1 },
      ...fieldColumns,
    ],
    defaultSort: null,
    defaultFilters: [],
  };
  // Normalizacja przez schemat (defaulty formattingRules/quickFilters/defaultViewMode) —
  // ten sam wzorzec co `templateListConfigFromLegacy`: zapis zasiewu musi być
  // deterministyczny pod dedup snapshotów i odcisk „nietknięty".
  const parsed = listConfigSchema.safeParse(seeded);
  return parsed.success ? parsed.data : (seeded as unknown as ListConfig);
}
